import { SessionStore } from './session.store';
import { LoginRequest } from '../../models/login/login.request';
import { AuthService } from '../../services/login/auth.service';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { SessionQuery } from './session.query';
import { LoginErrorModel } from '../../models/login/LoginErrorModel';
import { Observable, of } from 'rxjs';
import { PersonalBackendService } from '../../services/personal/personal-backend.service';
import { LogoutInfoModel } from '../../models/login/logout-info.model';
import { ToastService } from '../../services/toast-service';
import { Router } from '@angular/router';
import { resetStores } from '@datorama/akita';
import { LocalStorageService } from '../../services/local-storage.service';
import { TokenService } from '../../services/token.service';
import { StorageKeys } from '../../models/storage-keys';
import { SideMenuQuery } from '../side-menu/side-menu.query';
import { ChronoUserBackendService } from '../../services/chrono-user/chrono-user-backend.service';
import { ChronoVersionEnum } from '../../models/client-models';
import { LangaugeService } from '../../services/language.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  constructor(
    private sessionStore: SessionStore,
    private authService: AuthService,
    @Inject('persistStorage') private persistStorage,
    private sessionQuery: SessionQuery,
    private personalBackendService: PersonalBackendService,
    private chronoUserBackendService: ChronoUserBackendService,
    private sideMenuQuery: SideMenuQuery,
    private router: Router,
    private localStorageService: LocalStorageService,
    private ngZone: NgZone,
    private toastService: ToastService,
    private languageService: LangaugeService
  ) {
    this.clearError();
  }

  dontShowSplash() {
    this.sessionStore.update((state) => ({
      ...state,
      showSplash2Sek: false,
    }));
  }

  isLoggedIn(): boolean {
    return (
      this.sessionQuery.getPersonID() !== undefined &&
      this.sessionQuery.getPersonID() !== 0
    );
  }

  /**
   * Handle Login Request and update state accordingly
   *
   * @param loginRequest
   */
  login(loginRequest: LoginRequest): void {
    this.sessionStore.setLoading(true);
    this.authService
      .login(loginRequest)
      .pipe(
        map((response) => {
          this.localStorageService.setItem(
            StorageKeys.token,
            response.user.token
          );
          TokenService.token = response.user.token;
          response.user.token = '';
          this.localStorageService.setItem(
            StorageKeys.userInfo,
            JSON.stringify(response.user)
          );

          this.setLanguage(response.user.language);
          this.languageService.setLocale(response.user.language);

          let redirectUrl = this.sessionQuery.getValue()?.redirectUrl;

          if (response.user.id > 0) {
            redirectUrl = '/badge';
          } else {
            redirectUrl = '/persons';
          }
          //NOTE - crashes / endless loading on redirect to order
          // else if (response.user.id !== 0) {
          //   redirectUrl = '/orders';
          // }

          this.sessionStore.update((state) => ({
            ...state,
            showSplash2Sek: true,
            redirectUrl,
            user: response.user,
            chronoVersion: response.chronoVersion,
            uuid: loginRequest.UniqueID,
            successful: response.successful,
            navigateToDefaultUrl: true,
          }));

          this.getPersonalAuthorization();
        }),
        catchError((error: HttpErrorResponse) => {
          this.sessionStore.setError<LoginErrorModel>(error.error);
          return of(error.error); // needed because catchError expects a result.
        }),
        tap(() => this.sessionStore.setLoading(false))
      )
      .subscribe();
  }

  finishLoginNavigation() {
    this.sessionStore.update((state) => ({
      ...state,
      navigateToDefaultUrl: false,
    }));
  }

  /**
   * When not logged in or JWT is not valid redirect to login page.
   */
  redirectToLogin(url: string): void {
    this.sessionStore.update((state) => ({ ...state, redirectUrl: url }));
    this.ngZone.run(() => {
      this.router.navigateByUrl('login');
    });
  }

  clearError(): void {
    this.sessionStore.setError({} as LoginErrorModel);
  }

  setValidationError(message: string) {
    this.sessionStore.setError<LoginErrorModel>({
      general: message,
    } as LoginErrorModel);
  }

  /**
   * Get authorization of Person.
   */
  getPersonalAuthorization() {
    if (this.sessionQuery.getValue().user?.id) {
      this.personalBackendService
        .getPersonAuthorization(this.sessionQuery.getValue().user?.id)
        .subscribe({
          next: (data) => {
            this.sessionStore.update((state) => ({
              ...state,
              user: {
                ...state.user,
                authorization: data,
              },
            }));
          },
          error: (error) => this.sessionStore.setError(error.error),
        });
    }
  }

  getChronoUserAuthorization() {
    if (this.sessionQuery.getValue().user?.user?.id) {
      if (this.sessionQuery.getValue().user?.user?.id !== 0) {
        this.chronoUserBackendService
          .getUserById(this.sessionQuery.getValue().user?.user?.id)
          .subscribe({
            next: (data) => {
              this.sessionStore.update((state) => ({
                ...state,
                user: {
                  ...state.user,
                  user: data.resultObject,
                },
              }));
            },
            error: (error) => this.sessionStore.setError(error.error),
          });
      }
    }
  }

  /**
   * Logout person and delete used serialnumber.
   * Destroy saved cache.
   */
  logout() {
    this.sessionStore.update((state) => ({
      ...state,
      showSplash2Sek: true,
    }));

    const logOutInfo = {
      uniqueID: this.sessionQuery.getValue()?.uuid,
      personID: this.sessionQuery.getValue()?.user?.id,
    } as LogoutInfoModel;

    this.localStorageService.setItem(StorageKeys.userInfo, '');
    this.localStorageService.setItem(StorageKeys.token, '');

    resetStores();
    this.persistStorage.clearStore();

    this.authService.logout(logOutInfo).subscribe({
      next: (value) => {
        this.toastService.presentInfoToast({ message: value.message });
        setTimeout(() => window.location.reload(), 0);
      },
      error: (error) => {
        this.toastService.presentErrorToast({ message: error.error.message });
        setTimeout(() => window.location.reload(), 0);
      },
    });
  }

  /**
   * Update ComeAndGo authorisation.
   *
   * @param value new authorisation value.
   */
  setComeAndGoEdit(value: boolean): void {
    this.sessionStore.update((state) => ({
      ...state,
      user: {
        ...state.user,
        authorization: {
          ...state.user.authorization,
          comeAndGoEdit: value,
        },
      },
    }));
  }

  /**
   * Check if personal number is valid and active.
   *
   * @param personNumber Number of person to check.
   */
  checkPersonNr(personNumber: string): Observable<boolean> {
    return this.authService
      .checkInputUser(personNumber)
      .pipe(catchError(() => of(false)));
  }

  /**
   * Update language of user.
   *
   * @param ln new language.
   */
  setLanguage(ln: string) {
    this.sessionStore.update((state) => ({
      ...state,
      user: {
        ...state.user,
        language: ln,
      },
    }));
  }

  /**
   * Read settings to show personal list or not.
   */
  getPersonalListPrivacySettings() {
    this.authService.getListPrivacySettings().subscribe((value) => {
      this.sessionStore.update((state) => ({
        ...state,
        showPersonalList: value,
      }));
    });
  }

  getVisumTimeSetting() {
    this.authService.getVisumTimeSetting().subscribe((value) => {
      this.sessionStore.update((state) => ({
        ...state,
        visumWithTime: value,
      }));
    });
  }

  setChronoVersion(version: ChronoVersionEnum) {
    this.sessionStore.update((state) => ({
      ...state,
      chronoVersion: version,
    }));
  }

  /**
   * Is app running on a native device.
   *
   * @param value true if its running on a native device.
   */
  nativeDevice(value: boolean) {
    this.sessionStore.update((state) => ({
      ...state,
      nativeDevice: value,
    }));
  }

  setRedirectUrl(value: string) {
    this.sessionStore.update((state) => ({
      ...state,
      redirectUrl: value,
    }));
  }

  toggleMenu() {
    this.sessionStore.update((state) => ({
      ...state,
      menuToggle: !state.menuToggle,
    }));
  }

  navigateToDefaultUrl() {
    const url = this.sessionQuery.getValue()?.redirectUrl;
    this.ngZone.run(() => {
      this.router.navigateByUrl(url);
    });
  }
}
